import * as XLSXStyle from 'xlsx-js-style';
import {
  applyDescuentosColumnLabelsFromApi,
  formatPeriodoDescuentosLine,
  mapApiDescuentosTotalesToNormIds,
  mergeDescuentosEncabezado,
  mergeDescuentosFootTotals,
  resolveDescuentosColumns,
  rowToDescuentosNorm,
  sumDescuentosTotals,
  withDescuentosPeriodoLine,
} from './descuentosCortesiasReportModel';
import { appendFiltrosSucursalesCajasExcel } from './reporteFiltrosEncabezadoExport';

/**
 * Encabezado compacto (sin responsable ni domicilio), alineado al diseño de impreso.
 * @returns {{ titleRowIndex: number }} índice 0-based de la fila del título del reporte, o -1
 */
function appendDescuentosEncabezadoExcel(aoa, encMerged, form) {
  let titleRowIndex = -1;
  const up = (s) => String(s || '').trim().toUpperCase();
  if (
    encMerged.bloqueTexto &&
    String(encMerged.bloqueTexto).trim() &&
    !encMerged.nombreComercial &&
    !encMerged.reporteTitulo
  ) {
    for (const line of String(encMerged.bloqueTexto).split('\n')) {
      const t = line.trim();
      if (t) aoa.push([t]);
    }
    aoa.push([]);
    return { titleRowIndex: -1 };
  }
  if (encMerged.nombreComercial) {
    aoa.push([up(encMerged.nombreComercial)]);
  }
  if (encMerged.rfc) {
    aoa.push([up(`RFC: ${encMerged.rfc}`)]);
  }
  if (encMerged.reporteTitulo) {
    titleRowIndex = aoa.length;
    aoa.push([up(encMerged.reporteTitulo)]);
  }
  if (encMerged.cortesias && String(encMerged.cortesias).trim()) {
    aoa.push([up(encMerged.cortesias)]);
  }
  if (encMerged.periodoTexto) {
    aoa.push([String(encMerged.periodoTexto)]);
  } else {
    aoa.push([formatPeriodoDescuentosLine(form.fechaInicio, form.fechaFin)]);
  }
  appendFiltrosSucursalesCajasExcel(aoa, encMerged, form);
  if (encMerged.turnosTexto) {
    aoa.push([`Turnos: ${encMerged.turnosTexto}`]);
  } else {
    aoa.push([`Turnos: ${form.turnoInicio ?? '—'} a ${form.turnoFin ?? '—'}`]);
  }
  aoa.push([]);
  return { titleRowIndex };
}

function excelRawValue(spec, value) {
  if (value == null || value === '') return '';
  if (spec.kind === 'money' || spec.kind === 'percent') {
    const n = Number(value);
    return Number.isNaN(n) ? '' : n;
  }
  return value;
}

/**
 * @param {object[]} rawRows
 * @param {{
 *   fechaInicio?: string,
 *   fechaFin?: string,
 *   cajaInicio?: number,
 *   cajaFin?: number,
 *   turnoInicio?: number,
 *   turnoFin?: number,
 *   encabezado?: object|null,
 *   encabezadoFallback?: object,
 *   reporteTitulo?: string,
 *   apiTotales?: object|null,
 *   columnas?: Record<string, string>|null,
 * }} options
 */
export function buildDescuentosCortesiasExcelPayload(rawRows, options = {}) {
  const {
    fechaInicio = '',
    fechaFin = '',
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
    encabezado: encApi,
    encabezadoFallback,
    sucursalNombre = '',
    reporteTitulo,
    apiTotales = null,
    columnas = null,
  } = options;

  const form = { fechaInicio, fechaFin, cajaInicio, cajaFin, turnoInicio, turnoFin };
  const encBase = mergeDescuentosEncabezado(encApi, encabezadoFallback);
  const sn = String(sucursalNombre || '').trim();
  if (sn && !String(encBase.nombreComercial || '').toUpperCase().includes(sn.toUpperCase())) {
    encBase.nombreComercial = encBase.nombreComercial
      ? `${encBase.nombreComercial} - ${sn.toUpperCase()}`
      : sn.toUpperCase();
  }
  const encMerged = withDescuentosPeriodoLine(
    reporteTitulo ? { ...encBase, reporteTitulo } : encBase,
    fechaInicio,
    fechaFin
  );

  const aoa = [];
  const { titleRowIndex } = appendDescuentosEncabezadoExcel(aoa, encMerged, form);

  const list = Array.isArray(rawRows) ? rawRows : [];
  const resolvedBase = resolveDescuentosColumns(list.length > 0 ? list[0] : null);
  const resolved = applyDescuentosColumnLabelsFromApi(resolvedBase, columnas);

  if (titleRowIndex >= 0 && aoa[titleRowIndex]) {
    const t = aoa[titleRowIndex][0];
    aoa[titleRowIndex] = resolved.map((_, i) => (i === 0 ? t : ''));
  }

  const encRows = aoa.length;
  const normRows = list.map((r) => rowToDescuentosNorm(r, resolved));
  const summed = sumDescuentosTotals(normRows, resolved);
  const totals = mergeDescuentosFootTotals(summed, mapApiDescuentosTotalesToNormIds(apiTotales), resolved);

  aoa.push(resolved.map((c) => c.label));
  const colHeaderRow = encRows;
  const dataStartRow = encRows + 1;

  for (const r of normRows) {
    aoa.push(resolved.map((c) => excelRawValue(c, r[c.id])));
  }

  const totalRow = dataStartRow + normRows.length;
  aoa.push(resolved.map((c) => (c.total ? totals[c.id] : '')));

  return { aoa, resolved, encRows, colHeaderRow, dataStartRow, totalRow, titleRowIndex };
}

function styleDescuentosSheet(ws, meta) {
  const { resolved, encRows, colHeaderRow, dataStartRow, totalRow, titleRowIndex } = meta;
  if (!resolved.length || colHeaderRow < 0) return;

  const nDataCol = resolved.length;
  if (titleRowIndex >= 0 && nDataCol > 0) {
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push({
      s: { r: titleRowIndex, c: 0 },
      e: { r: titleRowIndex, c: Math.max(nDataCol - 1, 0) },
    });
    for (let c = 0; c < nDataCol; c++) {
      const addr = XLSXStyle.utils.encode_cell({ r: titleRowIndex, c });
      const cell = ws[addr];
      if (cell) {
        cell.s = {
          ...(cell.s || {}),
          font: { bold: true },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          fill: { fgColor: { rgb: 'FFBFDBFE' } },
        };
      }
    }
  }

  const encEnd = encRows - 1;
  for (let r = 0; r <= encEnd; r++) {
    if (r === titleRowIndex) continue;
    if (nDataCol > 1) {
      if (!ws['!merges']) ws['!merges'] = [];
      ws['!merges'].push({ s: { r, c: 0 }, e: { r, c: nDataCol - 1 } });
    }
    const addr = XLSXStyle.utils.encode_cell({ r, c: 0 });
    const cell = ws[addr];
    if (cell) {
      cell.s = {
        ...(cell.s || {}),
        alignment: { horizontal: 'center', wrapText: true },
        font: { ...((cell.s && cell.s.font) || {}), color: { rgb: 'FF0F172A' } },
      };
    }
  }

  for (let c = 0; c < nDataCol; c++) {
    const align = resolved[c].align === 'right' ? 'right' : 'left';
    const th = XLSXStyle.utils.encode_cell({ r: colHeaderRow, c });
    const tc = ws[th];
    if (tc) {
      tc.s = {
        ...(tc.s || {}),
        font: { bold: true, color: { rgb: 'FFFFFFFF' } },
        alignment: { horizontal: align },
        fill: { fgColor: { rgb: 'FF0F2F59' } },
        border: { bottom: { style: 'thin', color: { rgb: 'FF000000' } } },
      };
    }

    for (let r = dataStartRow; r <= totalRow; r++) {
      const addr = XLSXStyle.utils.encode_cell({ r, c });
      const cell = ws[addr];
      if (!cell) continue;
      const base = {
        ...(cell.s || {}),
        alignment: { horizontal: align },
        font: { color: { rgb: 'FF0F172A' } },
      };
      if (r === totalRow) {
        const isTotal = resolved[c].total;
        cell.s = {
          ...base,
          font: { bold: true, color: { rgb: 'FF000000' } },
          border: isTotal
            ? {
                top: { style: 'double', color: { rgb: 'FF000000' } },
                bottom: { style: 'thin', color: { rgb: 'FF000000' } },
              }
            : { bottom: { style: 'thin', color: { rgb: 'FF000000' } } },
        };
      } else {
        cell.s = base;
      }
    }
  }
}

export function downloadDescuentosCortesiasExcelFile(rawRows, options, fileName, sheetName) {
  const payload = buildDescuentosCortesiasExcelPayload(rawRows, options);
  const { aoa, resolved, encRows, colHeaderRow, dataStartRow, totalRow, titleRowIndex } = payload;

  const ws = XLSXStyle.utils.aoa_to_sheet(aoa);
  if (resolved.length) {
    styleDescuentosSheet(ws, {
      resolved,
      encRows,
      colHeaderRow,
      dataStartRow,
      totalRow,
      titleRowIndex,
    });
  }

  const colCount = Math.max(1, aoa.reduce((m, row) => Math.max(m, row.length), 0));
  const wch = [14, 18, 12, 10, 22, 18, 14, 10, 14, 16, 14, 14];
  ws['!cols'] = Array.from({ length: colCount }, (_, i) => ({
    wch: wch[i] ?? 14,
  }));

  const wb = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(wb, ws, sheetName);
  XLSXStyle.writeFile(wb, fileName);
}
